import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { environment } from 'src/environments/environment'

export interface BaseProviderStatus {
  connected: boolean
  enabled: boolean
}

export interface ProviderStatusEntry {
  provider: string
  connected: boolean
  enabled: boolean
  accountNumberMasked?: string
  originAddress?: { countryCode?: string; cityName?: string; addressLine?: string; postalCode?: string; phone?: string }
  settings?: Record<string, unknown>
}

@Injectable()
export abstract class BaseShippingProviderService<TSettings, TStatus extends BaseProviderStatus> {
  protected readonly statusSubject = new BehaviorSubject<TStatus | null>(null)
  readonly status$ = this.statusSubject.asObservable()

  private readonly isLoadingSubject = new BehaviorSubject<boolean>(false)
  readonly isLoading$ = this.isLoadingSubject.asObservable()

  private readonly errorSubject = new BehaviorSubject<string | null>(null)
  readonly error$ = this.errorSubject.asObservable()

  readonly CREDENTIAL_PLACEHOLDER = '••••••••'

  protected readonly agentsUrl = environment.agentsUrl

  protected abstract readonly providerKey: string
  protected abstract readonly providerLabel: string

  constructor(
    protected readonly http: HttpClient,
    protected readonly builderService: BuilderService
  ) {}

  /**
   * Maps a raw provider status entry from the admin API into the provider's typed status shape.
   * @param entry - The provider entry returned by the shipping admin endpoint.
   * @returns The provider-specific typed status.
   */
  protected abstract mapStatus(entry: ProviderStatusEntry): TStatus

  /**
   * Builds the request body sent when saving the provider's settings.
   * @param projectId - The merchant's project ID.
   * @param settings - The provider-specific settings to persist.
   * @returns The request payload for the save endpoint.
   */
  protected abstract buildSaveBody(projectId: string, settings: TSettings): Record<string, unknown>

  /**
   * Returns the current cached status without triggering a reload.
   * @returns The latest published status, or null when not loaded.
   */
  getStatus(): TStatus | null {
    return this.statusSubject.getValue()
  }

  /**
   * Applies a pre-fetched provider status entry directly, skipping the network call.
   * Called by the parent component after a shared fetch to avoid redundant requests.
   * @param entry - The matching entry from the shared providers response, or null if not found.
   */
  applyEntry(entry: ProviderStatusEntry | null): void {
    this.statusSubject.next(entry ? this.mapStatus(entry) : null)
  }

  /**
   * Loads masked provider connection status from stacks-agents and publishes it through status$.
   * Prefer calling ShippingProvidersComponent.loadAllProviders() to share one request across providers.
   * @param projectId - The merchant's project ID.
   */
  async loadStatus(projectId: string): Promise<void> {
    this.isLoadingSubject.next(true)
    this.errorSubject.next(null)
    try {
      const headers = this.buildHeaders()
      const url = `${this.agentsUrl}shipping/admin/providers?projectId=${projectId}`
      const response = await this.http.get<{ data: ProviderStatusEntry[] }>(url, { headers }).toPromise()
      const entry = response!.data.find((p: ProviderStatusEntry) => p.provider === this.providerKey)
      this.statusSubject.next(entry ? this.mapStatus(entry) : null)
    } catch {
      this.errorSubject.next(`Failed to load ${this.providerLabel} status`)
    } finally {
      this.isLoadingSubject.next(false)
    }
  }

  /**
   * Saves provider settings through stacks-agents. Credentials are encrypted on the backend
   * before being written to Supabase — they never touch the database in plaintext.
   * @param projectId - The merchant's project ID.
   * @param settings - The full provider settings to save.
   */
  async save(projectId: string, settings: TSettings): Promise<void> {
    this.isLoadingSubject.next(true)
    this.errorSubject.next(null)
    try {
      const headers = this.buildHeaders()
      const isConnected = this.statusSubject.getValue()?.connected
      const body = this.buildSaveBody(projectId, settings)
      const url = `${this.agentsUrl}shipping/admin/providers/${this.providerKey}`
      if (isConnected) {
        await this.http.patch(url, body, { headers }).toPromise()
      } else {
        await this.http.post(url, body, { headers }).toPromise()
      }
      await this.loadStatus(projectId)
    } catch {
      this.errorSubject.next(`Failed to save ${this.providerLabel} settings`)
    } finally {
      this.isLoadingSubject.next(false)
    }
  }

  /**
   * Masks all credential fields with the placeholder.
   * Call this whenever the status is refreshed to ensure stale plaintext is never left visible.
   * @param settings - The mutable settings object whose credential fields will be masked.
   * @param credentialFields - The keys of TSettings that hold credential values.
   */
  maskCredentials(settings: TSettings, credentialFields: (keyof TSettings)[]): void {
    for (const field of credentialFields) {
      ;(settings as Record<string, unknown>)[field as string] = this.CREDENTIAL_PLACEHOLDER
    }
  }

  /**
   * Removes the provider configuration for the project through stacks-agents
   * and clears the local status.
   * @param projectId - The merchant's project ID.
   */
  async disconnect(projectId: string): Promise<void> {
    this.isLoadingSubject.next(true)
    this.errorSubject.next(null)
    try {
      const headers = this.buildHeaders()
      const url = `${this.agentsUrl}shipping/admin/providers/${this.providerKey}?projectId=${projectId}`
      await this.http.delete(url, { headers }).toPromise()
      this.statusSubject.next(null)
    } catch {
      this.errorSubject.next(`Failed to disconnect ${this.providerLabel}`)
    } finally {
      this.isLoadingSubject.next(false)
    }
  }

  /**
   * Builds the authorization headers using the current merchant token.
   * @returns Headers carrying the bearer token for stacks-agents.
   */
  private buildHeaders(): HttpHeaders {
    const token = this.builderService.getToken()
    return new HttpHeaders({ Authorization: `Bearer ${token}` })
  }
}
